import React, { useState, useEffect } from "react";
import {
  TextInputField,
  SelectionGroup,
  PrimaryButton,
} from "./components";
import { useToast } from "../../../../hooks/useToast";
import providersService from "../../../../api/services/providersService";

const languageOptions = [
  "English",
  "Hausa",
  "Yoruba",
  "Igbo",
  "French",
  "Chinese",
  "Zulu",
  "Akan",
  "Ebira",
  "Fulani",
  "Spanish",
  "Mandarin",
  "Ijebu",
  "Arabic",
  "German",
];

const specializationOptions = [
  "Historical",
  "Food",
  "Adventures",
  "Cultural",
  "Religious",
  "Eco-Tourism",
  "Photography",
  "Sports",
];

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function parsePositiveNumber(value) {
  if (value === undefined || value === null) return 0;
  const num = parseInt(value, 10);
  return isNaN(num) || num < 0 ? 0 : num;
}

export default function ExperiencePanel({ profile, onRequestReview }) {
  const toast = useToast();
  const [experienceForm, setExperienceForm] = useState({
    years: "",
    languages: [],
    specializations: [],
  });
  const [isSavingExperience, setIsSavingExperience] = useState(false);

  useEffect(() => {
    if (profile) {
      const expertiseRoot = profile.profile ?? profile;
      setExperienceForm({
        years: String(
          expertiseRoot.experienceYears ??
            expertiseRoot.experience_years ??
            expertiseRoot.years ??
            ""
        ),
        languages: normalizeList(
          expertiseRoot.languages ?? expertiseRoot.language ?? expertiseRoot.languageSpoken
        ),
        specializations: normalizeList(
          expertiseRoot.specializations ??
            expertiseRoot.specialization ??
            expertiseRoot.areas ??
            expertiseRoot.area
        ),
      });
    }
  }, [profile]);

  const validateExperienceForm = () => {
    const yearsValue = parsePositiveNumber(experienceForm.years);
    if (!yearsValue) {
      toast.error("Please enter your years of experience.");
      return false;
    }
    if (!experienceForm.languages.length) {
      toast.error("Please select at least one language.");
      return false;
    }
    if (!experienceForm.specializations.length) {
      toast.error("Please select at least one area of specialisation.");
      return false;
    }
    return true;
  };

  const handleSaveClick = () => {
    const yearsValue = parsePositiveNumber(experienceForm.years);
    if (!validateExperienceForm()) return;

    onRequestReview("experience", async () => {
      setIsSavingExperience(true);
      try {
        await providersService.updateExpertise({
          experienceYears: yearsValue,
          languages: experienceForm.languages,
          specializations: experienceForm.specializations,
        });
        toast.success("Experience details updated successfully.");
        return true;
      } catch (error) {
        toast.apiError(error, "Unable to update experience details.");
        return false;
      } finally {
        setIsSavingExperience(false);
      }
    });
  };

  const toggleLanguage = (lang) => {
    setExperienceForm((prev) => {
      const active = prev.languages.includes(lang);
      const updated = active
        ? prev.languages.filter((item) => item !== lang)
        : [...prev.languages, lang];
      return { ...prev, languages: updated };
    });
  };

  const toggleSpecialization = (spec) => {
    setExperienceForm((prev) => {
      const active = prev.specializations.includes(spec);
      const updated = active
        ? prev.specializations.filter((item) => item !== spec)
        : [...prev.specializations, spec];
      return { ...prev, specializations: updated };
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <TextInputField
          label="Years of Experience"
          value={experienceForm.years}
          onChange={(value) => {
            setExperienceForm((prev) => ({ ...prev, years: value }));
          }}
          type="number"
          inputMode="numeric"
          placeholder="e.g. 5"
        />
        <div className="space-y-5">
          <SelectionGroup
            label="Languages Spoken"
            count={experienceForm.languages.length}
            options={languageOptions}
            values={experienceForm.languages}
            onToggle={toggleLanguage}
          />
          <SelectionGroup
            label="Areas of Specialisation"
            count={experienceForm.specializations.length}
            options={specializationOptions}
            values={experienceForm.specializations}
            onToggle={toggleSpecialization}
          />
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <PrimaryButton
          onClick={handleSaveClick}
          disabled={isSavingExperience}
          label={isSavingExperience ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
}
